
import React, { useState, useEffect, useRef } from 'react';
import { generateStory, generateImage, getStoryAudio, playAudio, pcmToWavBase64 } from './services/geminiService';
import { getStoriesByUserId, saveStoryToDB, deleteStoryFromDB, getUserFromDB, saveUserToDB, loginUser, registerUser, loginWithGoogle, onAuthChange, logoutUser, checkEmailVerificationStatus, uploadBase64ToStorage, getPublicStory } from './services/storageService';
import { Story, UserInput, AppState, StoryPart, User, GeneratedStoryResponse } from './types';
import Header from './components/Header';
import StoryForm from './components/StoryForm';
import StoryDisplay from './components/StoryDisplay';
import Loader from './components/Loader';
import Library from './components/Library';
import AuthForm from './components/AuthForm';
import VerificationForm from './components/VerificationForm';
import EmailAction from './components/EmailAction';
import Profile from './components/Profile';
import Shop from './components/Shop';
import PromoBanner from './components/PromoBanner';

import { getAudioBlob } from './services/geminiService';

const App: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [currentView, setCurrentView] = useState<AppState>(AppState.IDLE);
  const [activeStory, setActiveStory] = useState<Story | null>(null);
  const [savedStories, setSavedStories] = useState<Story[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showKeySelector, setShowKeySelector] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [isAudioPaused, setIsAudioPaused] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isPublicView, setIsPublicView] = useState(false);
  const [showPromo, setShowPromo] = useState(false);
  
  const [genState, setGenState] = useState<{
    status: AppState;
    progress: number;
    total: number;
    timer: number;
    isGenerating: boolean;
    lastGeneratedId?: string;
  }>({
    status: AppState.IDLE,
    progress: 0,
    total: 6,
    timer: 0,
    isGenerating: false
  });

  const audioControllerRef = useRef<{ 
    close: () => Promise<void>, 
    pause: () => void, 
    resume: () => Promise<void> 
  } | null>(null);
  const timerIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    const checkApiKey = async () => {
      if (window.aistudio && typeof window.aistudio.hasSelectedApiKey === 'function') {
        const hasKey = await window.aistudio.hasSelectedApiKey();
        setShowKeySelector(!hasKey);
      }
    };
    checkApiKey();
  }, []);

  const handleOpenKeySelector = async () => {
    if (window.aistudio && typeof window.aistudio.openSelectKey === 'function') {
      await window.aistudio.openSelectKey();
      setShowKeySelector(false);
      // Rifreskojmë faqen ose provojmë përsëri? 
      // Për thjeshtësi, supozojmë se përdoruesi do të provojë përsëri veprimin.
    }
  };

  // Dëgjuesi kryesor i autentikimit
  useEffect(() => {
    const unsubscribe = onAuthChange((user) => {
      setCurrentUser(user);
      setIsInitialLoading(false);

      if (user?.justReceivedPromo && user.isVerified) {
        setShowPromo(true);
      }
      
      if (user) {
        if (!user.isVerified) {
          setCurrentView(AppState.VERIFYING);
        } else {
          loadLibrary(user.id);
          // Kalojmë te IDLE vetëm nëse jemi në pamje autentikimi
          setCurrentView(prev => {
            if (prev === AppState.VERIFYING) {
              window.location.href = 'https://perralla.com';
              return prev;
            }
            if (prev === AppState.AUTH) return AppState.IDLE;
            return prev;
          });
        }
      } else {
        setCurrentView(prev => (prev === AppState.SHARED_STORY ? prev : AppState.AUTH));
      }
    });

    return () => unsubscribe();
  }, []);

  // Kontrolli për përrallat e shpërndara (Shared Stories) dhe veprimet e email-it
  useEffect(() => {
    const initFromUrl = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      
      // 1. Kontrollo për Email Actions (Verifikim)
      const mode = urlParams.get('mode');
      const oobCode = urlParams.get('oobCode');
      
      if (mode && oobCode) {
        setCurrentView(AppState.EMAIL_ACTION);
        return;
      }

      // 2. Load Shared Story from URL
      const sharedStoryId = urlParams.get('story');
      
      if (sharedStoryId) {
        try {
          setIsInitialLoading(true);
          const story = await getPublicStory(sharedStoryId);
          if (story) {
            setActiveStory(story);
            setCurrentView(AppState.SHARED_STORY);
            setIsPublicView(true);
          } else {
            setError("Përralla nuk u gjet ose nuk është më publike.");
          }
        } catch (err) {
          console.error("Gabim gjatë ngarkimit të përrallës publike:", err);
          setError("Ndodhi një gabim gjatë ngarkimit të përrallës.");
        } finally {
          setIsInitialLoading(false);
        }
      }
    };

    initFromUrl();
  }, []);

  const loadLibrary = async (userId: string) => {
    try {
      const stories = await getStoriesByUserId(userId);
      setSavedStories(stories);
    } catch (e) {
      console.warn("Libraria cloud nuk u arrit, duke përdorur lokale.");
    }
  };

  const handleLogin = async (email: string, name: string, password?: string, isLogin: boolean = true) => {
    try {
        setError(null);
        if (isLogin) {
            await loginUser(email, password);
        } else {
            await registerUser(email, password, name);
        }
    } catch (err: any) {
        throw err;
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setError(null);
      await loginWithGoogle();
    } catch (err: any) {
      setError(`Gabim gjatë hyrjes me Google: ${err.message}`);
    }
  };

  const handleVerifySuccess = async () => {
    const verified = await checkEmailVerificationStatus();
    if (verified && currentUser) {
      const updatedUser = { ...currentUser, isVerified: true };
      setCurrentUser(updatedUser);
      // Redirect to official domain after verification
      window.location.href = 'https://perralla.com';
    }
  };

  const handleLogout = async () => {
    await logoutUser();
    setCurrentUser(null);
    setCurrentView(AppState.AUTH);
    setSavedStories([]);
  };

  const handleBuyTokens = async (amount: number) => {
    if (!currentUser) return;
    const updatedUser = { ...currentUser, tokens: currentUser.tokens + amount };
    await saveUserToDB(updatedUser);
    setCurrentUser(updatedUser);
    setCurrentView(AppState.IDLE);
  };

  const startGenerationTimer = () => {
    setGenState(prev => ({ ...prev, timer: 0 }));
    timerIntervalRef.current = window.setInterval(() => {
      setGenState(prev => ({ ...prev, timer: prev.timer + 1 }));
    }, 1000);
  };

  const stopGenerationTimer = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  };

  const stopAudio = () => {
    if (audioControllerRef.current) {
      audioControllerRef.current.close().catch(console.error);
      audioControllerRef.current = null;
    }
    setIsAudioPlaying(false);
    setIsAudioPaused(false);
  };

  const handleViewChange = (newView: AppState) => {
    if (currentUser && !currentUser.isVerified && newView !== AppState.VERIFYING) return;
    stopAudio();
    
    // Dismiss promo banner if navigating away from home
    if (newView !== AppState.IDLE) {
      setShowPromo(false);
    }

    if (newView !== AppState.READING_STORY && newView !== AppState.SHARED_STORY) {
      setActiveStory(null);
      setIsPublicView(false);
      // Pastro URL-në nëse kthehemi në home
      if (window.location.search.includes('story=')) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
    
    // Refresh library data when navigating to it
    if (newView === AppState.LIBRARY && currentUser) {
      loadLibrary(currentUser.id);
    }
    
    setCurrentView(newView);
  };

  const handleGenerateStory = async (input: UserInput) => {
    if (genState.isGenerating || !currentUser || currentUser.tokens < 1) return;

    const apiKey = process.env.GEMINI_API_KEY;
    if (!apiKey || apiKey === "undefined" || apiKey === "") {
      setError("Mungon çelësi i API (GEMINI_API_KEY). Ju lutem shtoni atë te Secrets në platformë (AI Studio ose Vercel) dhe rifilloni aplikacionin.");
      return;
    }

    try {
      setError(null);
      setGenState({
        status: AppState.GENERATING_STORY,
        progress: 0,
        total: 6,
        timer: 0,
        isGenerating: true
      });
      
      startGenerationTimer();
      
      // 1. GENERATE STORY TEXT
      const generatedData = await generateStory(
        input.characterName, 
        input.referencePrompt, 
        input.photoBase64, 
        input.storyType,
        input.ageCategory
      );
      
      const storyId = window.crypto.randomUUID();
      
      // 2. GENERATE IMAGES
      setGenState(prev => ({ ...prev, status: AppState.GENERATING_IMAGES, progress: 0 }));
      const finalParts: StoryPart[] = [];
      let storageFailures = 0;
      
      for (let i = 0; i < generatedData.parts.length; i++) {
        const part = generatedData.parts[i];
        setGenState(prev => ({ ...prev, progress: i }));
        
        try {
          const imageDataUrl = await generateImage(part.imagePrompt, input.photoBase64);
          const base64Image = imageDataUrl.includes(',') ? imageDataUrl.split(',')[1] : imageDataUrl;
          const storagePath = `PerrallaStorage/${currentUser.id}/${storyId}/image_${i}.jpg`;
          const imageUrl = await uploadBase64ToStorage(storagePath, base64Image, 'image/jpeg');
          
          if (imageUrl.startsWith('data:')) storageFailures++;
          
          finalParts.push({ text: part.text, imagePrompt: part.imagePrompt, imageUrl });
          setGenState(prev => ({ ...prev, progress: i + 1 }));
        } catch (imgErr: any) {
          console.warn(`Imazhi ${i} dështoi, duke përdorur placeholder:`, imgErr);
          finalParts.push({ 
            text: part.text, 
            imagePrompt: part.imagePrompt, 
            imageUrl: `https://picsum.photos/seed/fairytale_${i}/800/800` 
          });
        }
      }

      // 3. GENERATE AUDIO
      setGenState(prev => ({ ...prev, status: AppState.GENERATING_AUDIO, progress: 0, total: 1 }));
      const fullText = generatedData.parts.map(p => p.text).join(' ');
      
      let audioUrl = "";
      try {
        const pcmBase64 = await getStoryAudio(fullText, input.voiceType);
        const wavBase64 = pcmToWavBase64(pcmBase64);
        const audioStoragePath = `PerrallaStorage/${currentUser.id}/${storyId}/audio.wav`;
        audioUrl = await uploadBase64ToStorage(audioStoragePath, wavBase64, 'audio/wav');
        console.log("Audio URL e gjeneruar:", audioUrl.substring(0, 50) + "...");
        if (audioUrl.startsWith('data:')) storageFailures++;
      } catch (audioErr) {
        console.warn("Audio generation failed:", audioErr);
      }
      
      if (storageFailures > 0) {
        console.warn(`${storageFailures} ngarkime në Storage dështuan. Përralla do të ruhet lokalisht.`);
      }
      
      // 4. SAVE STORY
      const finalStory: Story = {
        id: storyId,
        userId: currentUser.id,
        title: generatedData.title,
        parts: finalParts,
        audioBase64: audioUrl,
        createdAt: Date.now()
      };
      
      const updatedUser = { ...currentUser, tokens: currentUser.tokens - 1 };
      await saveUserToDB(updatedUser);
      setCurrentUser(updatedUser);
      
      await saveStoryToDB(finalStory);
      await loadLibrary(currentUser.id);
      
      setActiveStory(finalStory);
      setCurrentView(AppState.READING_STORY);

    } catch (err: any) {
      setError(`Gabim: ${err.message || 'Ndodhi një problem gjatë krijimit'}`);
    } finally {
      stopGenerationTimer();
      setGenState(prev => ({ ...prev, isGenerating: false, status: AppState.IDLE }));
    }
  };

  const handleSelectStory = (selected: Story) => {
    stopAudio();
    setActiveStory(selected);
    setCurrentView(AppState.READING_STORY);
  };

  const handleToggleAudio = async () => {
    if (isAudioPlaying && !isAudioPaused) {
      // Pause
      if (audioControllerRef.current) {
        audioControllerRef.current.pause();
        setIsAudioPaused(true);
      }
      return;
    }

    if (isAudioPlaying && isAudioPaused) {
      // Resume
      if (audioControllerRef.current) {
        try {
          await audioControllerRef.current.resume();
          setIsAudioPaused(false);
        } catch (err) {
          console.error("Gabim gjatë rifillimit:", err);
          stopAudio();
        }
      }
      return;
    }

    // Start new playback
    if (!activeStory || !activeStory.audioBase64) return;
    try {
      setIsAudioLoading(true);
      console.log("Duke nisur leximin e përrallës:", activeStory.id);
      const controller = await playAudio(activeStory.audioBase64, () => {
        console.log("Leximi përfundoi.");
        setIsAudioPlaying(false);
        setIsAudioPaused(false);
        setIsAudioLoading(false);
        audioControllerRef.current = null;
      });
      audioControllerRef.current = controller;
      setIsAudioPlaying(true);
      setIsAudioPaused(false);
      setIsAudioLoading(false);
    } catch (err) {
      console.error("Gabim gjatë leximit:", err);
      setIsAudioPlaying(false);
      setIsAudioPaused(false);
      setIsAudioLoading(false);
    }
  };

  const handleDeleteStory = async (id: string) => {
    if (currentUser) {
      try {
        await deleteStoryFromDB(currentUser.id, id);
        await loadLibrary(currentUser.id);
        
        if (activeStory?.id === id) {
          stopAudio();
          setActiveStory(null);
          setCurrentView(AppState.LIBRARY);
        }
        console.log(`Përralla ${id} u fshi me sukses.`);
      } catch (err: any) {
        console.error("Gabim gjatë fshirjes:", err);
        setError(`Gabim gjatë fshirjes: ${err.message || 'Provoni përsëri'}`);
      }
    }
  };

  if (isInitialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader state={AppState.IDLE} progress={0} />
      </div>
    );
  }

  const containerMaxWidth = currentView === AppState.LIBRARY ? 'max-w-7xl' : 'max-w-4xl';

  return (
    <div className="min-h-screen py-6 px-4 md:py-10 flex flex-col items-center">
      {showPromo && currentView === AppState.IDLE && (
        <PromoBanner onClose={() => setShowPromo(false)} />
      )}
      <Header 
        onHome={() => handleViewChange(currentUser ? AppState.IDLE : AppState.AUTH)} 
        currentView={currentView} 
        onViewChange={(v) => handleViewChange(v as AppState)} 
        user={currentUser}
      />
        
        <main className={`w-full ${containerMaxWidth} flex flex-col gap-6 md:gap-10`}>
          {genState.isGenerating && (
            <div className="loader-container sticky top-4 z-[100] mb-4">
              <div className="glass-card rounded-[2rem] p-4 md:p-6 border border-white/20 shadow-2xl bg-indigo-950/80 backdrop-blur-2xl">
                <Loader state={genState.status} progress={genState.progress} total={genState.total} timer={genState.timer} compact={true} />
              </div>
            </div>
          )}

          {error && (
            <div className="p-4 rounded-2xl bg-red-500/20 border border-red-500/50 text-red-100 backdrop-blur-lg mb-6 animate-in fade-in zoom-in">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">{error}</p>
                  <button onClick={() => setError(null)} className="ml-4 text-xs font-bold uppercase tracking-widest opacity-60 hover:opacity-100">Mbyll ✕</button>
                </div>
                
                {(error.includes('403') || error.includes('leje') || error.includes('permission') || showKeySelector) && (
                  <div className="flex flex-col gap-2 pt-2 border-t border-red-500/30">
                    <p className="text-xs opacity-80">
                      Gjenerimi i imazheve dhe audios kërkon një çelës API të paguar (Paid API Key).
                    </p>
                    <button 
                      onClick={handleOpenKeySelector}
                      className="bg-yellow-400 text-indigo-950 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-yellow-300 transition-colors self-start"
                    >
                      Zgjidh Çelësin API
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="w-full animate-in fade-in slide-in-from-bottom-4 duration-700">
            {currentView === AppState.AUTH && (
              <AuthForm onLogin={handleLogin} onGoogleLogin={handleGoogleLogin} />
            )}

            {currentView === AppState.VERIFYING && currentUser && (
              <VerificationForm 
                email={currentUser.email} 
                onVerify={handleVerifySuccess} 
                onCancel={handleLogout} 
              />
            )}

            {currentView === AppState.EMAIL_ACTION && (
              <EmailAction 
                mode={new URLSearchParams(window.location.search).get('mode') || ''} 
                oobCode={new URLSearchParams(window.location.search).get('oobCode') || ''} 
                onComplete={() => {
                  window.history.replaceState({}, document.title, window.location.pathname);
                  // Redirect to official domain after verification
                  window.location.href = 'https://perralla.com';
                }} 
              />
            )}

            {currentView === AppState.PROFILE && currentUser && (
              <Profile 
                user={currentUser} 
                storyCount={savedStories.length} 
                onLogout={handleLogout} 
                onGoToShop={() => setCurrentView(AppState.SHOP)}
                onBack={() => setCurrentView(AppState.IDLE)}
              />
            )}

            {currentView === AppState.SHOP && currentUser && (
              <Shop 
                onBack={() => setCurrentView(AppState.IDLE)} 
                onBuyTokens={handleBuyTokens} 
                tokens={currentUser.tokens} 
                userId={currentUser.id}
              />
            )}

            {currentView === AppState.IDLE && currentUser && (
              <StoryForm 
                onSubmit={handleGenerateStory} 
                isBusy={genState.isGenerating} 
                tokens={currentUser.tokens}
                onGoToShop={() => setCurrentView(AppState.SHOP)}
              />
            )}

            {currentView === AppState.LIBRARY && (
              <Library 
                stories={savedStories} 
                onSelect={handleSelectStory} 
                onDelete={handleDeleteStory} 
              />
            )}

            {(currentView === AppState.READING_STORY || currentView === AppState.SHARED_STORY) && activeStory && (
              <StoryDisplay 
                story={activeStory} 
                isPlaying={isAudioPlaying} 
                isPaused={isAudioPaused}
                isLoading={isAudioLoading}
                isPublic={isPublicView}
                onToggleAudio={handleToggleAudio} 
                onBack={() => isPublicView ? handleViewChange(currentUser ? AppState.IDLE : AppState.AUTH) : setCurrentView(AppState.LIBRARY)} 
              />
            )}
          </div>
        </main>

        <div className="mt-12 mb-8 max-w-2xl mx-auto w-full px-4 no-print">
          <div className="glass-card rounded-2xl p-4 border border-white/5 bg-white/5 backdrop-blur-sm">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-yellow-400 text-xs">✨</span>
              <h4 className="text-[10px] font-black text-indigo-200 uppercase tracking-widest">Përditësimet e Fundit</h4>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-[9px] text-indigo-100/50 font-medium list-disc list-inside">
              <li>Folklor Shqiptar me mitologji të pasur 🇦🇱</li>
              <li>Ilustrime pa korniza (Full Bleed) 🖼️</li>
              <li>Konsistencë vizuale e personazheve 👤</li>
              <li>Zgjedhja e moshës për përralla smart 👶</li>
              <li>Emri i personalizuar i fëmijës ✨</li>
            </ul>
          </div>
        </div>

        <footer className="mt-auto pt-8 pb-8 text-indigo-200/50 text-[10px] no-print tracking-[0.3em] font-black uppercase flex flex-col items-center gap-2">
          <span>© 2026 PërrallAIme</span>
          <div className="flex items-center gap-2 opacity-70">
            <div className="w-1 h-1 rounded-full bg-green-500 animate-pulse"></div>
            <span className="text-[8px]">Mbrojtje Hibride Aktive</span>
          </div>
        </footer>
    </div>
  );
};

export default App;
